import i18next from "i18next";
import { Session } from "../models/Session";
import { InviteeType } from "../models/InviteeType";
import { Member } from "../../members/models/Member";
import { Group } from "../../members/models/Group";

/** One attendee to mark, resolved to a real member where possible. */
export interface RosterMember {
  memberId: string;
  fullName: string;
}

export interface RosterGroupSection {
  kind: "group";
  groupId: string;
  groupName: string;
  members: RosterMember[];
}

export interface RosterIndividualsSection {
  kind: "individuals";
  members: RosterMember[];
}

export type RosterSection = RosterGroupSection | RosterIndividualsSection;

/**
 * The flattened attendance roster for a session: every member to mark, grouped
 * for display (one section per invited group plus an "Individuals" section), and
 * deduplicated so each person appears — and is counted — exactly once.
 */
export interface AttendanceRoster {
  sections: RosterSection[];
  memberIds: string[];
  totalCount: number;
}

function fullName(member: Member): string {
  return `${member.firstName} ${member.lastName}`.trim();
}

/**
 * Builds the attendance roster from a session's invitees. Group invitees are
 * flattened into their member rows; a member invited more than once (e.g. via two
 * groups, or individually and through a group) is assigned to the first invitee
 * that introduces them, so they yield a single row and a single visit.
 *
 * Two Group-invitee shapes exist: legacy rows where InvitedUserId is a group id
 * (expanded here via the members' group memberships), and per-member rows where
 * InvitedUserId is a member id and Type=Group only records provenance (the
 * canonical backend/seeder shape) — those resolve as plain member rows.
 */
/** A member who left (erased / deleted) but is still referenced — never leak the id. */
const formerMemberLabel = (): string =>
  i18next.isInitialized ? i18next.t("sessions.formerMember", { defaultValue: "Former member" }) : "Former member";

export function buildAttendanceRoster(
  session: Session,
  members: Member[],
  groups: Group[]
): AttendanceRoster {
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const assigned = new Set<string>();
  const sections: RosterSection[] = [];
  const individuals: RosterMember[] = [];

  for (const invitee of session.invitees) {
    // One-off invitees belong to a single occurrence's attendance, not the plan roster.
    if (invitee.type === InviteeType.OneOff) continue;
    if (invitee.type === InviteeType.Group && !memberMap.has(invitee.invitedUserId)) {
      const groupId = invitee.invitedUserId;
      const group = groupMap.get(groupId);
      const rosterMembers: RosterMember[] = [];
      for (const member of members) {
        if (!member.groups.some((g) => g.id === groupId)) continue;
        if (assigned.has(member.id)) continue;
        assigned.add(member.id);
        rosterMembers.push({ memberId: member.id, fullName: fullName(member) });
      }
      sections.push({
        kind: "group",
        groupId,
        groupName: group?.name ?? `Group ${groupId.substring(0, 8)}`,
        members: rosterMembers,
      });
    } else {
      const memberId = invitee.invitedUserId;
      if (assigned.has(memberId)) continue;
      assigned.add(memberId);
      const member = memberMap.get(memberId);
      individuals.push({
        memberId,
        fullName: member ? fullName(member) : formerMemberLabel(),
      });
    }
  }

  if (individuals.length > 0) {
    sections.push({ kind: "individuals", members: individuals });
  }

  return {
    sections,
    memberIds: Array.from(assigned),
    totalCount: assigned.size,
  };
}
